import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Mail, Phone, KeyRound, ArrowLeft, CheckCircle2 } from "lucide-react";

// Public endpoints don't require auth cookies, but we still hit the same origin
const publicApi = axios.create({
    baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
    withCredentials: true,
    headers: { "Content-Type": "application/json" },
});

function formatErr(e) {
    return e?.response?.data?.detail || e?.message || "Something went wrong";
}

function Shell({ title, subtitle, testid, children }) {
    return (
        <div className="min-h-screen bg-[color:var(--bl-bg)] flex items-center justify-center p-6 grain" data-testid={testid}>
            <Toaster position="top-right" theme="light" richColors />
            <div className="w-full max-w-md">
                <Link to="/login" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-[color:var(--bl-text-dim)] hover:text-[color:var(--bl-takealot-blue)] mb-6" data-testid="back-to-login">
                    <ArrowLeft className="w-3 h-3" /> Back to sign in
                </Link>
                <div className="bg-[color:var(--bl-surface)] border border-[color:var(--bl-border)] rounded-sm p-8 shadow-sm">
                    <div className="flex items-center gap-3 mb-1">
                        <img src="/bull.png" alt="" className="w-10 h-10 object-contain rounded-full" />
                        <div>
                            <div className="font-serif-display text-xl text-[color:var(--bl-text)] leading-none">Takealot Insight</div>
                            <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--bl-takealot-orange)] mt-1.5 font-medium">Brothers Coffee</div>
                        </div>
                    </div>
                    <h1 className="mt-6 font-serif-display text-3xl text-[color:var(--bl-text)]">{title}</h1>
                    {subtitle && <p className="text-sm text-[color:var(--bl-text-dim)] mt-1">{subtitle}</p>}
                    <div className="mt-6">{children}</div>
                </div>
            </div>
        </div>
    );
}

const inputCls = "w-full bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] px-3 py-2.5 rounded-sm text-sm text-[color:var(--bl-text)] focus:outline-none focus:border-[color:var(--bl-takealot-blue)]";
const btnCls = "w-full bg-[color:var(--bl-takealot-blue)] text-white font-medium py-2.5 rounded-sm hover:brightness-110 transition disabled:opacity-50 inline-flex items-center justify-center gap-2";

export function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await publicApi.post("/account/forgot-password", { email });
            setSent(true);
        } catch (err) {
            toast.error(formatErr(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Shell title="Forgot password?" subtitle="Enter your account email — we'll send you a reset link." testid="forgot-password-page">
            {sent ? (
                <div className="text-sm text-[color:var(--bl-text)] space-y-3" data-testid="forgot-password-success">
                    <div className="inline-flex items-center gap-2 text-[color:var(--bl-success)]">
                        <CheckCircle2 className="w-4 h-4" />
                        <span className="font-medium">Check your inbox</span>
                    </div>
                    <p className="text-[color:var(--bl-text-dim)]">If <span className="font-mono-num">{email}</span> is registered, we've sent a reset link. It's valid for 24 hours.</p>
                    <Link to="/login" className="inline-block mt-2 text-[color:var(--bl-takealot-blue)] text-xs uppercase tracking-wider hover:underline">Back to sign in →</Link>
                </div>
            ) : (
                <form onSubmit={submit}>
                    <label className="block text-xs uppercase tracking-wider text-[color:var(--bl-text-dim)] mb-2">Email address</label>
                    <input data-testid="forgot-password-email" type="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls + " mb-5"} />
                    <button data-testid="forgot-password-submit" type="submit" disabled={loading} className={btnCls}>
                        <Mail className="w-4 h-4" /> {loading ? "Sending…" : "Send reset link"}
                    </button>
                    <div className="text-center mt-4 text-xs">
                        <Link to="/forgot-email" className="text-[color:var(--bl-takealot-orange)] hover:underline">Forgot which email you used?</Link>
                    </div>
                </form>
            )}
        </Shell>
    );
}

export function ForgotEmail() {
    const [phone, setPhone] = useState("");
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await publicApi.post("/account/forgot-email", { phone });
            setSent(true);
        } catch (err) {
            toast.error(formatErr(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Shell title="Forgot your email?" subtitle="Enter your phone number — we'll send your sign-in email to the address on file." testid="forgot-email-page">
            {sent ? (
                <div className="text-sm text-[color:var(--bl-text)] space-y-3" data-testid="forgot-email-success">
                    <div className="inline-flex items-center gap-2 text-[color:var(--bl-success)]">
                        <CheckCircle2 className="w-4 h-4" />
                        <span className="font-medium">Check your inbox</span>
                    </div>
                    <p className="text-[color:var(--bl-text-dim)]">If <span className="font-mono-num">{phone}</span> matches an account, we've sent the sign-in email to the address on that account.</p>
                    <Link to="/login" className="inline-block mt-2 text-[color:var(--bl-takealot-blue)] text-xs uppercase tracking-wider hover:underline">Back to sign in →</Link>
                </div>
            ) : (
                <form onSubmit={submit}>
                    <label className="block text-xs uppercase tracking-wider text-[color:var(--bl-text-dim)] mb-2">Phone number</label>
                    <input data-testid="forgot-email-phone" type="tel" required autoFocus placeholder="0821234567" value={phone} onChange={(e) => setPhone(e.target.value)} className={inputCls + " mb-5"} />
                    <button data-testid="forgot-email-submit" type="submit" disabled={loading} className={btnCls}>
                        <Phone className="w-4 h-4" /> {loading ? "Sending…" : "Email me my sign-in address"}
                    </button>
                    <div className="text-center mt-4 text-xs">
                        <Link to="/forgot-password" className="text-[color:var(--bl-takealot-orange)] hover:underline">Know your email? Reset password instead</Link>
                    </div>
                </form>
            )}
        </Shell>
    );
}

export function ResetPassword({ mode = "reset" }) {
    // mode: "reset" (forgot flow) or "setup" (new-user welcome flow)
    const [params] = useSearchParams();
    const nav = useNavigate();
    const token = params.get("token") || "";
    const [tokenInfo, setTokenInfo] = useState(null);
    const [checking, setChecking] = useState(true);
    const [pw, setPw] = useState("");
    const [confirm, setConfirm] = useState("");
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (!token) {
            setChecking(false);
            return;
        }
        publicApi
            .get(`/account/reset-token/${encodeURIComponent(token)}`)
            .then((r) => setTokenInfo(r.data))
            .catch((err) => toast.error(formatErr(err)))
            .finally(() => setChecking(false));
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        if (pw.length < 8) {
            toast.error("Password must be at least 8 characters");
            return;
        }
        if (pw !== confirm) {
            toast.error("Passwords do not match");
            return;
        }
        setLoading(true);
        try {
            await publicApi.post("/account/reset-password", { token, new_password: pw });
            setDone(true);
            setTimeout(() => nav("/login"), 2500);
        } catch (err) {
            toast.error(formatErr(err));
        } finally {
            setLoading(false);
        }
    };

    const isSetup = mode === "setup" || tokenInfo?.purpose === "welcome";
    const title = isSetup ? "Set your password" : "Reset your password";
    const subtitle = isSetup
        ? "Welcome — choose the password you'll sign in with from now on."
        : "Choose a new password for your account. It must be at least 8 characters.";

    return (
        <Shell title={title} subtitle={subtitle} testid="reset-password-page">
            {checking ? (
                <div className="text-sm text-[color:var(--bl-text-dim)]">Checking link…</div>
            ) : !token || !tokenInfo ? (
                <div className="text-sm text-[color:var(--bl-text)] space-y-3" data-testid="reset-password-invalid">
                    <p className="text-[color:var(--bl-danger)] font-medium">This link is invalid or has expired.</p>
                    <p className="text-[color:var(--bl-text-dim)]">Request a fresh one — reset links are valid for 24 hours.</p>
                    <Link to="/forgot-password" className={btnCls} data-testid="request-new-link">Request a new link</Link>
                </div>
            ) : done ? (
                <div className="text-sm text-[color:var(--bl-text)] space-y-3" data-testid="reset-password-success">
                    <div className="inline-flex items-center gap-2 text-[color:var(--bl-success)]">
                        <CheckCircle2 className="w-4 h-4" />
                        <span className="font-medium">Password updated</span>
                    </div>
                    <p className="text-[color:var(--bl-text-dim)]">Redirecting you to sign in…</p>
                </div>
            ) : (
                <form onSubmit={submit}>
                    <div className="mb-4 text-xs text-[color:var(--bl-text-dim)]">
                        Setting the password for <span className="font-mono-num text-[color:var(--bl-text)]">{tokenInfo.email}</span>
                    </div>
                    <label className="block text-xs uppercase tracking-wider text-[color:var(--bl-text-dim)] mb-2">New password</label>
                    <input data-testid="reset-new-password" type="password" required minLength={8} autoFocus value={pw} onChange={(e) => setPw(e.target.value)} className={inputCls + " mb-3"} />

                    <label className="block text-xs uppercase tracking-wider text-[color:var(--bl-text-dim)] mb-2">Confirm password</label>
                    <input data-testid="reset-confirm-password" type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls + " mb-5"} />

                    <button data-testid="reset-submit" type="submit" disabled={loading} className={btnCls}>
                        <KeyRound className="w-4 h-4" /> {loading ? "Saving…" : isSetup ? "Set password & sign in" : "Update password"}
                    </button>
                </form>
            )}
        </Shell>
    );
}
